import type { UserDetail, UserListItem, UserStatus } from '@/domain/users/entities/user.entity'
import { unwrapPayload } from '@/data/shared/unwrap_payload'
import type { UserDetailDto, UserListItemDto } from '../dto/user.dto'

const LIST_KEYS = ['items', 'users', 'data', 'results'] as const

function normalizeStatus(raw: unknown): UserStatus {
  const s = String(raw ?? 'active').toLowerCase()
  return s === 'inactive' ? 'inactive' : 'active'
}

function mapListItemDto(dto: UserListItemDto): UserListItem {
  const record = dto as Record<string, unknown>
  const phone =
    dto.phoneNo ??
    dto.phone_no ??
    (typeof record.phoneNumber === 'string' ? record.phoneNumber : '')
  const active =
    record.isActive === true ||
    record.isActive === 'true' ||
    dto.status === 'active'

  return {
    id: String(dto.id ?? ''),
    name: String(dto.name ?? ''),
    email: String(dto.email ?? ''),
    phoneNo: String(phone ?? ''),
    status: dto.status ? normalizeStatus(dto.status) : active ? 'active' : 'inactive',
    createdAt: String(dto.createdAt ?? dto.created_at ?? new Date().toISOString()),
  }
}

export function mapUserListItemDto(dto: UserListItemDto): UserListItem {
  return mapListItemDto(dto)
}

export function mapUserDetailDto(dto: UserDetailDto): UserDetail {
  const base = mapListItemDto(dto)
  return {
    ...base,
    countryCode:
      typeof dto.countryCode === 'string'
        ? dto.countryCode
        : typeof dto.country_code === 'string'
          ? dto.country_code
          : undefined,
    bio: typeof dto.bio === 'string' ? dto.bio : undefined,
    gender:
      typeof dto.gender === 'string'
        ? dto.gender
        : typeof dto.gender === 'object' &&
            dto.gender !== null &&
            'name' in (dto.gender as object)
          ? String((dto.gender as { name?: string }).name ?? '')
          : undefined,
    dateOfBirth:
      typeof dto.dateOfBirth === 'string'
        ? dto.dateOfBirth
        : typeof dto.date_of_birth === 'string'
          ? dto.date_of_birth
          : typeof dto.dob === 'string'
            ? dto.dob
            : undefined,
    imageUrl:
      typeof dto.imageUrl === 'string'
        ? dto.imageUrl
        : typeof dto.image_url === 'string'
          ? dto.image_url
          : undefined,
  }
}

function findUserArray(data: unknown): UserListItemDto[] {
  if (Array.isArray(data)) return data as UserListItemDto[]
  if (!data || typeof data !== 'object') return []

  const root = data as Record<string, unknown>
  for (const key of LIST_KEYS) {
    const candidate = root[key]
    if (Array.isArray(candidate)) return candidate as UserListItemDto[]
  }

  const unwrapped = unwrapPayload(data)
  for (const key of LIST_KEYS) {
    const candidate = unwrapped[key]
    if (Array.isArray(candidate)) return candidate as UserListItemDto[]
  }

  if (Array.isArray(unwrapped.items)) return unwrapped.items as UserListItemDto[]

  return []
}

export function extractUserList(data: unknown): UserListItem[] {
  return findUserArray(data).map(mapUserListItemDto).filter(u => Boolean(u.id) || Boolean(u.email))
}

export function mapUserDetailResponse(data: unknown): UserDetail {
  const o = unwrapPayload(data)
  return mapUserDetailDto(o as UserDetailDto)
}
